// The diagram of one model: a migration's model or a context's current model.
import { GitBranchPlus, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModel } from '../state/useModel';
import { refKey, select, useWorkspace, type ModelRef } from '../state/workspace';
import { openSketch } from '../state/dock';
import { branchSketch } from '../state/sketches';
import { Canvas, Divider, ToolButton, type CanvasSelection, type CanvasTarget } from './Canvas';
import { buildGraph } from './graph';
import { cardFontsReady, measureText } from './measure';
import { useDiagramSettings } from './settings';
import { layoutInput, useLayout } from './useLayout';

/** True once the card fonts are loaded, so measured card widths are right. */
export function useCardFonts(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void cardFontsReady().then(() => setReady(true));
  }, []);
  return ready;
}

export function DiagramView({ modelRef }: { modelRef: ModelRef }) {
  const key = refKey(modelRef);
  const { model, error, loading } = useModel(modelRef);
  const settings = useDiagramSettings();
  const source = useWorkspace((s) => s.source);
  const selection = useWorkspace((s) => (s.selection && refKey(s.selection.ref) === key ? s.selection.value : null));
  const fontsReady = useCardFonts();
  // In focus the focused card shows every property, whatever the property mode.
  const [focused, setFocused] = useState(false);
  const properties = focused ? 'all' : settings.properties;

  const graph = useMemo(
    () => (model && fontsReady ? buildGraph(model.result.doc.model, { properties, collapseJoins: settings.collapseJoins, measure: measureText }) : null),
    [model, fontsReady, properties, settings.collapseJoins],
  );
  const input = useMemo(() => (graph ? layoutInput(graph) : null), [graph]);
  const layoutKey = source ? `${source.id}|${modelRef.setKey}|${settings.properties}` : null;
  const { positions, save, relayout } = useLayout(layoutKey, input);

  const selected = useMemo<CanvasSelection>(
    () => ({
      node: selection?.kind === 'entity' ? selection.key : null,
      row: selection?.kind === 'property' ? `${selection.entity}::${selection.property}` : null,
      edge: selection?.kind === 'relationship' ? `rel:${selection.index}` : null,
    }),
    [selection],
  );

  const onSelect = useCallback(
    (target: CanvasTarget | null) => {
      if (!target) select(modelRef, null);
      else if (target.kind === 'node') select(modelRef, { kind: 'entity', key: target.node.key });
      else if (target.kind === 'row') select(modelRef, { kind: 'property', entity: target.node.key, property: target.row.id });
      else if (target.edge.relationship !== null) select(modelRef, { kind: 'relationship', index: target.edge.relationship });
    },
    [modelRef],
  );

  // Center on an entity when the explorer or command palette asks for it.
  const request = useWorkspace((s) => (s.reveal && refKey(s.reveal.ref) === key ? s.reveal : null));
  const reveal = useMemo(() => (request ? { id: request.key.split('.')[0]!, nonce: request.nonce, focus: request.focus, deletion: request.deletion } : null), [request]);

  if (error) {
    return <div className="flex h-full items-center justify-center p-6 text-center text-muted">{error}</div>;
  }
  if (loading || !graph || !positions) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted">
        <Loader2 size={16} className="animate-spin" /> Reading model…
      </div>
    );
  }

  const doc = model!.result.doc;
  return (
    <Canvas
      graph={graph}
      positions={positions}
      selected={selected}
      onSelect={onSelect}
      onMoved={save}
      onRelayout={() => void relayout()}
      fitKey={key}
      reveal={reveal}
      onFocusChange={setFocused}
      model={doc.model}
      tools={
        <>
          <Divider />
          <ToolButton label="Start a sketch from this model" onClick={() => void branchSketch(modelRef).then((s) => s && openSketch(s.id))}>
            <GitBranchPlus size={15} />
          </ToolButton>
        </>
      }
      status={
        <>
          {Object.keys(doc.model.entities).length} entities · {doc.model.relationships.length} relationships
          {model?.result.efVersion && <> · EF Core {model.result.efVersion}</>}
        </>
      }
    />
  );
}
